import {
    Box,
    Button,
    Center,
    Flex,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Tag,
    TagCloseButton,
    useToast,
} from '@chakra-ui/react';
import { useUpdateClientSingleAliasMutation } from 'core/API/productsApi';
import { useTranslation } from 'helpers/languages/translations';
import { ChangeEvent, useState } from 'react';
import icons from 'shared/icons';
import { tagStyle, linkStyle } from 'shared/styles';
import { TAliasProps } from '../../AliasContainer';

const AliasEditModal = ({
    currentAlias,
    isOpenEdit,
    readonly,
    onCloseEdit,
    onOpenDelete,
    setCurrentAlias,
}: TAliasProps) => {
    const translate = useTranslation();
    const toast = useToast();

    const [editInputValue, setEditInputValue] = useState('');
    const [updateAliase, { isLoading: isEditLoading }] = useUpdateClientSingleAliasMutation();

    const handleDeleteAliasEdit = (alias: string) => {
        const newCurrentAlias = { mainAlias: currentAlias!.mainAlias, aliases: [...currentAlias!.aliases] };
        newCurrentAlias.aliases = newCurrentAlias.aliases.filter((e) => e !== alias);
        setCurrentAlias(newCurrentAlias);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEditInputValue(event.target.value);
    };

    const handleAddAlias = () => {
        const newCurrentAlias = { mainAlias: currentAlias!.mainAlias, aliases: [...currentAlias!.aliases] };
        if (!newCurrentAlias.aliases.includes(editInputValue) && editInputValue !== newCurrentAlias.mainAlias) {
            newCurrentAlias.aliases.push(editInputValue);
            setCurrentAlias(newCurrentAlias);
            setEditInputValue('');
        }
    };

    const handleApplyAliasEdition = async () => {
        try {
            const res = await updateAliase({
                key: currentAlias!.mainAlias,
                aliases: currentAlias!.aliases,
            });

            if ('error' in res) throw new Error(translate('alias.update.error') as string);

            onCloseEdit();
            toast({
                title: translate('alias.update.success'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleOpenDeleteAlias = () => {
        onOpenDelete();
        onCloseEdit();
    };

    const tagList =
        currentAlias &&
        currentAlias.aliases.map((tagName) => (
            <Tag {...tagStyle} key={tagName} mb="4px" mr="4px">
                {tagName}
                {!readonly && <TagCloseButton onClick={() => handleDeleteAliasEdit(tagName)} />}
            </Tag>
        ));

    return (
        <Modal size="xl" isOpen={isOpenEdit} onClose={onCloseEdit}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{translate('alias.update.header')}</ModalHeader>
                <ModalCloseButton disabled={isEditLoading} />
                <ModalBody>
                    {currentAlias && (
                        <Box>
                            <Flex border="1px solid #CAC5C4" borderRadius="4px" mb="24px">
                                <Center
                                    px="4px"
                                    width="20%"
                                    fontWeight="bold"
                                    borderRight="1px solid #CAC5C4"
                                    py="8px"
                                    wordBreak="break-word"
                                >
                                    {currentAlias.mainAlias}
                                </Center>
                                <Flex flexWrap="wrap" width="80%" p="8px">
                                    {tagList}
                                </Flex>
                            </Flex>
                            {!readonly && (
                                <Flex justifyContent="space-between">
                                    <Box flex="1" mr="16px">
                                        <Input
                                            value={editInputValue}
                                            onChange={handleInputChange}
                                            size="sm"
                                            placeholder={translate('alias.update.placeholder') as string}
                                        />
                                    </Box>
                                    <IconButton
                                        size="sm"
                                        aria-label="add-alias"
                                        icon={<Icon as={icons.plus} />}
                                        onClick={handleAddAlias}
                                    />
                                </Flex>
                            )}
                        </Box>
                    )}
                </ModalBody>

                <ModalFooter fontSize="sm" justifyContent="space-between">
                    <Flex>
                        {!readonly && (
                            <Box {...linkStyle} color="red.500" onClick={handleOpenDeleteAlias}>
                                {translate('alias.delete')}
                            </Box>
                        )}
                    </Flex>
                    <Flex flex="1" justifyContent="flex-end">
                        <Button size="sm" variant="outline" mr={3} isDisabled={isEditLoading} onClick={onCloseEdit}>
                            {translate('alias.update.cancel')}
                        </Button>
                        <Button
                            onClick={handleApplyAliasEdition}
                            size="sm"
                            isLoading={isEditLoading}
                            colorScheme="blue"
                            bg="blueMain"
                            isDisabled={currentAlias?.aliases.length === 0}
                        >
                            {translate('alias.update.apply')}
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AliasEditModal;
